import { KeyValue } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  DefaultsStateKey,
  ExcludedTradeStatusesKey,
  LegAttribute,
  PricingResultsInfo,
  ReportingCurrencyStateKey,
  ResultAttribute,
  UseDefaultsStateKey,
  UserPreferencesState,
  UserPreferencesStateKey
} from '@approot/models';
import {
  AdminService,
  PricingExtraService,
  ProductsConfigService,
  TradesCaptureService,
  UserStorageService
} from '@approot/services';
import { SharedModule } from '@approot/shared';
import { SubscriptionSink } from '@approot/utilities';
import { cloneDeep } from 'lodash';

type PricingDisplayPref = { header: string; options: any };
type PricingDisplaySettings = Record<string, boolean>;

@Component({
  standalone: true,
  selector: 'app-user-preferences',
  imports: [SharedModule],
  templateUrl: './user-preferences.component.html',
  styleUrls: ['./user-preferences.component.scss']
})
export class UserPreferencesComponent implements OnInit, OnDestroy {
  // ALL User preferences should be moved into the userPreferences save object eventually!!
  @Input() public isModalOpened = false;
  @Output() public userPrefOpenChanged = new EventEmitter<boolean>();
  public form: FormGroup;
  public soundForm: FormGroup;
  public tradeStatusesForm: FormGroup;
  public tradeStatusNames: string[];
  public productAttributes: string[] = [];
  public attributeOptions: string[] = [];
  public currencies: string[] = [];
  public currentDefaults = [];
  public validDefaultAttributes = [
    'targetProfitType',
    'targetProfitUnit',
    'koType',
    'dayCountConvention',
    'barrierObservationType',
    'cross',
    'position',
    'direction',
    'optionType',
    'upperBarrierObservationType',
    'lowerBarrierObservationType',
    'lowerBarrierType',
    'upperBarrierType',
    'barrierType',
    'valuationAdjustmentCurrency',
    'premiumCurrency',
    'midPvCurrency',
    'cutOff',
    'fixingSource'
  ];
  public pricingDisplayPrefs: PricingDisplayPref[];
  public pricingDisplaySettings: PricingDisplaySettings;
  public otherSettings: Record<string, any> = {};

  private subsink = new SubscriptionSink();

  constructor(
    private fb: FormBuilder,
    private productsConfigService: ProductsConfigService,
    private pricingExtraService: PricingExtraService,
    private tradeCaptureService: TradesCaptureService,
    private adminService: AdminService,
    private userStorageService: UserStorageService
  ) {
    this.form = this.fb.group({
      productAttributes: this.fb.control(''),
      options: this.fb.control(''),
      useDefaults: this.fb.control<boolean>(this.adminService.useDefaults)
    });
    this.soundForm = this.fb.group({
      sounds: this.fb.control<boolean>(true),
      soundLevel: this.fb.control<string>('quiet')
    });

    this.setupTradeStatusesForm();

    this.subsink.sink = this.soundForm.valueChanges.subscribe(val => {
      this.updateOtherSettings(val);
      this.userStorageService.doPing();
    });
  }

  public ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  public ngOnInit() {
    let userPreferences = this.userStorageService.getItem<UserPreferencesState>(
      UserPreferencesStateKey
    );
    this.currentDefaults = this.adminService.attributeDefaults;
    this.currencies = this.pricingExtraService.currencies;
    this.otherSettings = userPreferences.OtherSettings || {};
    this.soundForm.patchValue(
      {
        sounds: this.otherSettings.sounds ?? true,
        soundLevel: this.otherSettings.soundLevel ?? 'quiet'
      },
      { emitEvent: false }
    );

    let products = this.getAvailableProductAttributes();
    this.productAttributes = products;

    this.subsink.sink =
      this.tradeCaptureService.allTradeCaptureDataLoaded.subscribe(isLoaded => {
        // listen to if all trade capture related data has been loaded
        this.allTradeCaptureDataHasBeenLoaded(isLoaded);
      });

    [this.pricingDisplayPrefs, this.pricingDisplaySettings] =
      this.processPricingResultsInfo(
        this.productsConfigService.pricingResultsInfo,
        userPreferences.PricingDisplayPrefs
      );
    this.subscribeToProductAttributes();
  }

  private setupTradeStatusesForm() {
    this.tradeStatusesForm = this.fb.group({});

    const excludedTradeStatuses =
      this.userStorageService.getItem<string[]>(ExcludedTradeStatusesKey) || [];

    this.tradeCaptureService.getTradeStatuses().subscribe(r => {
      this.tradeStatusNames = r.tradeStatuses.sort();
      const def = this.tradeStatusNames.reduce((acc, s, idx) => {
        const set = !excludedTradeStatuses.includes(s);
        return { ...acc, [idx]: this.fb.control<boolean>(set) };
      }, {});
      this.tradeStatusesForm = this.fb.group(def);
      this.subsink.sink = this.tradeStatusesForm.valueChanges.subscribe(val => {
        const excluded = (Object.entries(val) as [string, boolean][]).reduce(
          (acc, [k, v]) => (v ? acc : [...acc, this.tradeStatusNames[k]]),
          [] as string[]
        );
        this.userStorageService.setItem(ExcludedTradeStatusesKey, excluded);
      });
    });
  }

  public orderOriginal = (
    _a: KeyValue<any, any>,
    _b: KeyValue<any, any>
  ): number => {
    return 0;
  };

  public updatePricingDisplaySettings(name: string) {
    this.pricingDisplaySettings[name] = !this.pricingDisplaySettings[name];
    const userPreferences =
      this.userStorageService.getItem<UserPreferencesState>(
        UserPreferencesStateKey
      );
    userPreferences.PricingDisplayPrefs = this.pricingDisplaySettings;
    this.userStorageService.setItem(UserPreferencesStateKey, userPreferences);
  }

  private processPricingResultsInfo(
    pricingResultsInfo: PricingResultsInfo,
    displayPrefs: any
  ): [PricingDisplayPref[], PricingDisplaySettings] {
    if (!displayPrefs) {
      displayPrefs = {};
    }
    const prefs: PricingDisplayPref[] = [];
    const settings: PricingDisplaySettings = {};
    let entries: ResultAttribute[];
    for (entries of Object.values(pricingResultsInfo)) {
      if (!entries.length) {
        continue;
      }
      // the "Extra" section is a bit wierd - skip for now
      const isExtraSection = entries.find(
        v => v.header === 'true' && v.shortName === 'extraResults'
      );
      if (isExtraSection) {
        continue;
      }
      const header = entries.find(v => v.header === 'true').longName;
      const options = entries
        .filter(v => v.header !== 'true')
        .map(opt => {
          const existing = displayPrefs[opt.shortName];
          let enabled: boolean;
          if (existing == null) {
            enabled = true;
          } else {
            enabled = !!existing;
          }
          return {
            shortName: opt.shortName,
            longName: opt.longName,
            enabled
          };
        });

      options.forEach(opt => {
        settings[opt.shortName] = opt.enabled;
      });

      prefs.push({ header, options });
    }

    return [prefs, settings];
  }

  private allTradeCaptureDataHasBeenLoaded(isLoaded: boolean) {
    if (isLoaded) {
      // if all trade capture data has been loaded add to valid attributes
      this.pushToValidAttributes('executionVenue');
      this.pushToValidAttributes('settlementConvention');
      this.pushToValidAttributes('fixingConvention');
      this.pushToValidAttributes('tradeStatus');
      this.pushToValidAttributes('calculationAgent');
      this.pushToValidAttributes('valuationAdjustmentInFavourOf');
      this.productAttributes = this.getAvailableProductAttributes(); // refresh the product Attributes
    }
  }

  private pushToValidAttributes(attributeName: string) {
    // push an attribute to the array list, check to see if the attribute is valid first
    const tradeAttributes =
      this.productsConfigService.productInfo.tradeAttributes;
    if (
      tradeAttributes.find(attribute => attribute.shortName === attributeName)
    ) {
      this.validDefaultAttributes.push(attributeName);
    }
  }

  public changeInReportingCurrency(currency: string) {
    // when attempting to change the currency stored
    // if there is no currency selected, then set the stored currency to the default usd else
    // store the new currency set
    if (currency) {
      this.userStorageService.setItem(ReportingCurrencyStateKey, currency);
    } else {
      this.userStorageService.setItem(ReportingCurrencyStateKey, 'USD');
    }
  }

  public get defaultReportingCurrency(): string {
    // gets the default currency stored, if none is stored, then get USD
    const reportingCurrency =
      this.userStorageService.getItem<string>(ReportingCurrencyStateKey) || '';
    return reportingCurrency ? reportingCurrency : 'USD';
  }

  // returns unique possible values for an attribute

  private cutOffsOrFixings(cutOffsOrFixingSources: string) {
    // fixing source/ cut off available longNames returned
    return this.productsConfigService.cutOffTimesAndFixingSources[
      cutOffsOrFixingSources
    ]
      .filter(val => {
        if (
          (cutOffsOrFixingSources === 'cutOffs' &&
            (val.code === 'NYC_1000' || val.code === 'TKO_1500')) ||
          (cutOffsOrFixingSources === 'fixingSources' &&
            val.code === 'BBG_1000') ||
          val.code === 'BBG_1500' ||
          val.code === 'WMR_1600'
        ) {
          return true;
        }

        return false;
      })
      .map(cutFix => cutFix.longName);
  }

  private validDefaults(attributeName: string): string[] {
    return [
      ...new Set(
        this.productsConfigService.productInfo.productSpecifications.flatMap(
          x =>
            x.attributes
              .filter(y => y.shortName === attributeName)
              .flatMap(z => z.value)
        )
      )
    ].filter(v => !!v) as string[];
  }

  private validTradesDefaults(attributeName: string): string[] {
    const attributeControl =
      this.productsConfigService.productInfo.tradeAttributes.find(
        control => control.shortName === attributeName
      );

    if (attributeControl) {
      return attributeControl.value;
    } else {
      return null;
    }
  }

  public useDefaultsToggleChange(event) {
    this.userStorageService.setItem(UseDefaultsStateKey, event.target.checked);
  }

  public addToDefaults() {
    let attribute = this.form.get('productAttributes').value;
    let defaultOption = this.form.get('options').value;
    if (attribute && defaultOption) {
      let newDefaultSetting = {};
      newDefaultSetting['key'] = attribute;
      newDefaultSetting['value'] = defaultOption;
      this.currentDefaults.push(newDefaultSetting);

      let defaultsToSave = cloneDeep(this.currentDefaults);

      this.formatFixingSourceOrCutOff(defaultsToSave, 'cutOffs'); // format cut off
      this.formatFixingSourceOrCutOff(defaultsToSave, 'fixingSources'); // format fixing source
      this.userStorageService.setItem(DefaultsStateKey, defaultsToSave);
      // to dynamically update the dropdown, productAttributes needs to be placed in new Object
      this.productAttributes = this.getAvailableProductAttributes();
      this.form.get('productAttributes').setValue('');
      this.form.get('options').setValue('');
    }
  }

  private formatFixingSourceOrCutOff(
    defaults: { key: string; value: string }[],
    fixingSourcesOrCutOffs: string
  ) {
    // longName to code name formatting
    let field = defaults.find(
      defaultAttribute =>
        defaultAttribute.key ===
        (fixingSourcesOrCutOffs === 'fixingSources'
          ? 'Fixing Source'
          : 'CutOff')
    );

    if (field) {
      field.value = this.productsConfigService.cutOffTimesAndFixingSources[
        fixingSourcesOrCutOffs
      ].find(y => y.longName === field.value).code;
    }
  }

  // available product attributes are those specified in the validDefaultAttributes array
  // and have not yet been set by the user
  private getAvailableProductAttributes() {
    return this.productsConfigService.productInfo.attributesDisplayList
      .filter((y: LegAttribute) => {
        return (
          this.validDefaultAttributes.includes(y.shortName) &&
          !(this.currentDefaults.findIndex(x => x.key === y.longName) > -1)
        );
      })
      .map(x => {
        return x.longName;
      });
  }

  public removeFromDefaults(defaultAttribute) {
    this.currentDefaults = this.currentDefaults.filter(
      x => x.key !== defaultAttribute.key
    );
    this.userStorageService.setItem(DefaultsStateKey, this.currentDefaults);
    // to dynamically update the dropdown, productAttributes needs to be placed in new Object
    this.productAttributes = this.getAvailableProductAttributes();
  }

  public modalChanged(event) {
    this.userPrefOpenChanged.emit(event);
  }

  public updateOtherSettings(value: any) {
    const settings = this.userStorageService.getItem<UserPreferencesState>(
      UserPreferencesStateKey
    );
    settings.OtherSettings = { ...this.otherSettings, ...value };
    this.userStorageService.setItem(UserPreferencesStateKey, settings);
  }

  private subscribeToProductAttributes() {
    this.subsink.sink = this.form
      .get('productAttributes')
      .valueChanges.subscribe(val => {
        this.form.get('options').setValue('');
        switch (val) {
          case 'Currency Pair':
            this.attributeOptions =
              this.productsConfigService.cutOffTimesAndFixingSources.currencyPairInfo.map(
                x => x.currencyPair
              );
            break;
          case 'V.A. Currency':
          case 'Premium Currency':
          case 'Mid PV Currency':
            this.attributeOptions = this.currencies;
            break;
          case 'Position':
            // get an array of distinct possible options for position
            this.attributeOptions = this.validDefaults('position');
            break;
          case 'Direction':
            // get an array of distinct possible options for direction
            this.attributeOptions = this.validDefaults('direction');
            break;
          case 'Option Type':
            // get an array of distinct possible options for direction
            this.attributeOptions = this.validDefaults('optionType');
            break;
          case 'Upper Barrier Obs. Type':
            // get an array of distinct possible options for direction
            this.attributeOptions = this.validDefaults(
              'upperBarrierObservationType'
            );
            break;
          case 'Lower Barrier Type':
            // get an array of distinct possible options for direction
            this.attributeOptions = this.validDefaults('lowerBarrierType');
            break;
          case 'Upper Barrier Type':
            // get an array of distinct possible options for direction
            this.attributeOptions = this.validDefaults('upperBarrierType');
            break;
          case 'Lower Barrier Obs. Type':
            // get an array of distinct possible options for lowerBarrierObservationType
            this.attributeOptions = this.validDefaults(
              'lowerBarrierObservationType'
            );
            break;
          case 'Barrier Obs. Type':
            // get an array of distinct possible options for lowerBarrierObservationType
            this.attributeOptions = this.validDefaults(
              'barrierObservationType'
            );
            break;
          case 'Barrier Type':
            // get an array of distinct possible options for barrierType
            this.attributeOptions = this.validDefaults('barrierType');
            break;
          case 'Target Profit Type':
            // get an array of distinct possible options for barrierType
            this.attributeOptions = this.validDefaults('targetProfitType');
            break;
          case 'Target Profit Unit':
            // get an array of distinct possible options for barrierType
            this.attributeOptions = this.validDefaults('targetProfitUnit');
            break;
          case 'Knock-Out Type':
            // get an array of distinct possible options for barrierType
            this.attributeOptions = this.validDefaults('koType');
            break;
          case 'Day Count Convention':
            // get an array of distinct possible options for barrierType
            this.attributeOptions = this.validDefaults('dayCountConvention');
            break;
          case 'Exec. Venue':
            // get an array of distinct possible options for barrierType
            this.attributeOptions = this.validTradesDefaults('executionVenue');
            break;
          case 'Settlement Convention':
            // get an array of distinct possible options for barrierType
            this.attributeOptions = this.validTradesDefaults(
              'settlementConvention'
            );
            break;
          case 'Fixing Convention':
            // get an array of distinct possible options for barrierType
            this.attributeOptions =
              this.validTradesDefaults('fixingConvention');
            break;
          case 'Trade Status':
            // get an array of distinct possible options for barrierType
            this.attributeOptions = this.validTradesDefaults('tradeStatus');
            break;
          case 'Calculation Agent':
            // get an array of distinct possible options for barrierType
            this.attributeOptions =
              this.validTradesDefaults('calculationAgent');
            break;
          case 'V.A. In Favour Of':
            // get an array of distinct possible options for barrierType
            this.attributeOptions = this.validTradesDefaults(
              'valuationAdjustmentInFavourOf'
            );
            break;
          case 'CutOff':
            this.attributeOptions = this.cutOffsOrFixings('cutOffs');
            break;
          case 'Fixing Source':
            this.attributeOptions = this.cutOffsOrFixings('fixingSources');
            break;
          default:
            this.attributeOptions = [];
        }
      });
  }
}
